import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { Menu, X } from "lucide-react";
import BRAND from "@/config/brand";

const publicLinks = [
  { to: "/", label: "Home" },
  { to: "/fanfest", label: "FanFest" },
  { to: "/directory", label: "Directory" },
  { to: "/prizes", label: "Prizes" },
  { to: "/sponsors", label: "Sponsors" },
  { to: "/faq", label: "FAQ" },
];

export function PublicNav() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const dashboardHref = user && user.role === "admin" ? "/admin"
    : user && user.role === "business" ? "/business" : "/dashboard";
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#070c1a]/85 border-b border-adn-border pt-[env(safe-area-inset-top)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center group">
          <img src="/logo.png" alt="MyADN Local FanFest"
            width="512" height="341"
            className="h-14 sm:h-16 md:h-[72px] w-auto object-contain
                       drop-shadow-[0_0_18px_rgba(0,211,127,0.55)]
                       group-hover:drop-shadow-[0_0_28px_rgba(0,211,127,0.85)]
                       transition-all duration-300 group-hover:scale-[1.03]"
            style={{ imageRendering: "auto" }}
          />
        </Link>
        <nav className="hidden md:flex items-center gap-1 ml-6">
          {publicLinks.map(l => (
            <NavLink key={l.to} to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({isActive}) =>
                `px-3 py-2 text-sm rounded hover:text-primary transition-colors ${isActive ? "text-primary" : "text-white/80"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto hidden md:flex items-center gap-3">
          {user ? (
            <>
              <button onClick={() => nav(dashboardHref)}
                data-testid="nav-dashboard-btn"
                className="text-sm px-4 py-2 rounded-sm border border-adn-border hover:border-primary hover:text-primary">
                Dashboard
              </button>
              <button onClick={async () => { await logout(); nav("/"); }}
                data-testid="nav-logout-btn"
                className="text-sm px-4 py-2 rounded-sm hover:text-primary">
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-btn"
                className="text-sm px-4 py-2 rounded-sm hover:text-primary">
                Login
              </Link>
              <Link to="/register" data-testid="nav-register-btn"
                className="text-sm px-5 py-2 rounded-sm bg-primary text-black font-semibold hover:brightness-110">
                Enter Now
              </Link>
            </>
          )}
        </div>
        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen(v => !v)}
          className="ml-auto md:hidden p-2 rounded border border-adn-border">
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-adn-border bg-[#0b132a]" data-testid="mobile-menu">
          {publicLinks.map(l => (
            <Link key={l.to} to={l.to}
              onClick={() => setOpen(false)}
              className="block px-6 py-3 border-b border-adn-border text-sm hover:text-primary">
              {l.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link to={dashboardHref} onClick={() => setOpen(false)}
                className="block px-6 py-3 border-b border-adn-border text-primary">Dashboard</Link>
              <button onClick={async () => { await logout(); setOpen(false); nav("/"); }}
                className="w-full text-left px-6 py-3 border-b border-adn-border">
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)}
                className="block px-6 py-3 border-b border-adn-border">Login</Link>
              <Link to="/register" onClick={() => setOpen(false)}
                className="block px-6 py-3 border-b border-adn-border text-primary font-semibold">Enter Now</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const linkCls = "inline-block py-1.5 text-white/70 hover:text-primary hover:underline underline-offset-4 transition-colors";
  const { brand } = useBrand();
  const poweredBy = brand.poweredBy || BRAND.poweredBy;
  return (
    <footer className="border-t border-adn-border mt-16 bg-[#050912]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid gap-8 md:grid-cols-4 text-sm">
        <div>
          <img src="/logo.png" alt={brand.name || "MyADN Local FanFest"}
            className="h-24 w-auto object-contain drop-shadow-[0_0_28px_rgba(0,211,127,0.55)]" />
          <p className="text-white/60 mt-3">{brand.motto}</p>
        </div>
        <div>
          <div className="font-bebas text-lg text-primary">Explore</div>
          <ul className="mt-2 space-y-0.5">
            <li><Link data-testid="footer-directory" to="/directory" className={linkCls}>Business Directory</Link></li>
            <li><Link data-testid="footer-prizes" to="/prizes" className={linkCls}>Prizes</Link></li>
            <li><Link data-testid="footer-fanfest" to="/fanfest" className={linkCls}>FanFest™ Landing</Link></li>
            <li><Link data-testid="footer-leaderboard" to="/leaderboard" className={linkCls}>Leaderboard</Link></li>
            <li><Link data-testid="footer-faq" to="/faq" className={linkCls}>FAQ</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-bebas text-lg text-primary">Business</div>
          <ul className="mt-2 space-y-0.5">
            <li><Link data-testid="footer-sponsors" to="/sponsors" className={linkCls}>Sponsor Info</Link></li>
            <li><Link data-testid="footer-become-sponsor" to="/register?role=business" className={linkCls}>Become a Sponsor</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-bebas text-lg text-primary">Legal</div>
          <ul className="mt-2 space-y-0.5">
            <li><Link data-testid="footer-rules" to="/rules" className={linkCls}>Official Rules</Link></li>
            <li><Link data-testid="footer-privacy" to="/privacy" className={linkCls}>Privacy Policy</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-adn-border px-4 sm:px-6 py-6 text-xs text-white/50 max-w-7xl mx-auto">
        <p>No purchase necessary. Void where prohibited or restricted by law, including Puerto Rico, Guam, the U.S. Virgin Islands, American Samoa, and the Northern Mariana Islands. Must be 18 or older. One entry per person/household. Grand Prize winner must check in through the app at a participating Official Watch Party Partner™ location in Jacksonville, FL on July 19, 2026 during the official check-in window (12:00 PM – 2:45 PM EDT). Total ARV of all prizes: approximately $4,899 USD.</p>
        <p className="mt-2">ADN Local™ is not sponsored by or affiliated with FIFA, the World Cup, any soccer federation, airline, hotel, or travel provider unless expressly stated. FIFA and World Cup marks belong to their respective owners.</p>
        <div className="mt-6 text-center space-y-1" data-testid="footer-bottom">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} ADN Local, LLC. All rights reserved.</p>
          <p>
            <Link to="/rules" data-testid="footer-see-rules" className="underline hover:text-primary">
              See Official Rules
            </Link>
          </p>
          <p>
            Powered by{" "}
            <a href={poweredBy.url} target="_blank" rel="noopener noreferrer"
               data-testid="footer-powered-by"
               className="text-primary hover:underline underline-offset-4">
              {poweredBy.name}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-black focus:px-3 focus:py-2 focus:rounded">
        Skip to main content
      </a>
      <PublicNav />
      <main id="main-content" className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
